/**
 * **What the Ankiety pane is ABOUT, in words.** One decision, three readers.
 *
 * The pane draws either one occurrence's report or two of them side by side, and three places have
 * to agree about which: the head above the content, the tab's live region, and the stream's own
 * „Porównujesz…" line. They were three separate constructions of the same sentence — the head built
 * it, `buildSurveyCompare` built a second one for the subtitle, and the live region did not exist at
 * all — so this module answers it once.
 *
 * ── It is also what lets the head stand OUTSIDE the animated box ─────────────────────────────
 * The pane's content is keyed on its subject and swapped by Motion, which means it is unmounted and
 * rebuilt on every press. The head used to ride inside that box, so the control the trainer had just
 * pressed — „zamień strony", „oznacz jako przejrzaną" — was destroyed under their finger and focus
 * fell to `<body>`. The head is now rendered from this view beside the keyed box and reconciles in
 * place; nothing it draws is rebuilt because the content changed.
 *
 * ── The comparison's head is read off the COLUMNS, not off the built table ───────────────────
 * Everything in it — both ordinals, both dates, the span between them, the form's title — is on
 * `SurveyHistoryView` already. Building the whole comparison to learn its title would mean building
 * it twice, once for the head and once for the body.
 *
 * Pure: no React, no DOM, no query hook, no i18next import.
 */

import { latenessLabel, type SurveyColumnView, type SurveyHistoryView } from '~/features/survey-report';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * What the pane is doing.
 *
 * `picking` is compare mode before two occurrences have been checked: the stream is a list of
 * checkboxes and the pane is still a report, which is a real state and not a half-open comparison.
 */
export type SurveyPaneMode = 'report' | 'picking' | 'compare';

export interface SurveyPaneView {
  readonly mode: SurveyPaneMode;
  /** The two columns the comparison is of, oldest-first as the caller ordered them. */
  readonly pair: { readonly left: SurveyColumnView; readonly right: SurveyColumnView } | null;
  readonly title: string;
  readonly subtitle: string;
  /** „Porównujesz: wybierz 2" — the stream head's line while compare mode is on. `null` otherwise. */
  readonly modeNote: string | null;
  /**
   * The ONE sentence the tab's live region publishes for this state.
   *
   * A screen reader is told what the pane is about, because pressing a row on the left changes the
   * whole right-hand side and nothing else on the page says so. Empty when there is nothing to be
   * about — an empty client, or a read still in flight.
   */
  readonly announcement: string;
}

export interface SurveyPaneDeps {
  readonly t: Translate;
  /**
   * The two occurrences checked for a comparison, or `null` when the trainer is not comparing. An
   * empty array is compare mode with nothing picked yet, and is not the same as `null`.
   */
  readonly comparing: readonly number[] | null;
}

/** Two occurrences compared, and no more. */
const PAIR = 2;

export function describeSurveyPane(
  history: SurveyHistoryView,
  deps: SurveyPaneDeps,
): SurveyPaneView {
  const { t, comparing } = deps;

  const picked =
    comparing === null || comparing.length < PAIR
      ? null
      : comparing.map((id) => history.columns.find((column) => column.id === id));
  const left = picked?.[0];
  const right = picked?.[1];

  if (left !== undefined && right !== undefined) {
    const title = t('web:clientDetail.surveys.compareTitle', {
      from: left.ordinalLabel,
      to: right.ordinalLabel,
    });
    return {
      mode: 'compare',
      pair: { left, right },
      title,
      subtitle: t('web:clientDetail.surveys.compareSubtitle', {
        // The FORM's title, not a per-occurrence one: both columns are occurrences of one form, so
        // the title was the same string on each of them.
        form: history.activeForm?.title ?? '',
        from: left.dateLabel,
        to: right.dateLabel,
        count: Math.abs(right.ordinal - left.ordinal),
      }),
      modeNote: t('web:clientDetail.surveys.comparePicked'),
      announcement: title,
    };
  }

  // Compare mode is on but the pair is not complete — or one of the two fell out of the view. The
  // pane is a report and the head says so; the mode line is what carries "you are still picking".
  const picking = comparing !== null;
  const modeNote = picking ? t('web:clientDetail.surveys.comparePickTwo') : null;
  const selected = history.selected;
  if (selected === null) {
    return { mode: picking ? 'picking' : 'report', pair: null, title: '', subtitle: '', modeNote, announcement: modeNote ?? '' };
  }

  // „5 paź · wypełniona 7 paź, 2 dni po terminie" (owner, 4 November 2026): the cycle's day and the
  // filling's, and how late in words. The ordinal and the send time it used to print („tydz. 9 ·
  // wysłana pon 14 wrz, 02:00") were the server's schedule, not something the trainer acts on.
  const meta =
    selected.filledLabel === null
      ? t('web:clientDetail.surveys.reportMetaOpen', {
          date: selected.dateLabel,
          status: selected.statusLabel,
        })
      : selected.status === 'Late'
        ? t('web:clientDetail.surveys.reportMetaLate', {
            date: selected.dateLabel,
            filled: selected.filledLabel,
            lateness: latenessLabel(selected.lateDays, t),
          })
        : t('web:clientDetail.surveys.reportMeta', {
            date: selected.dateLabel,
            filled: selected.filledLabel,
          });
  // „przejrzana" is the ABSENCE of a mark everywhere else on this tab, so the sentence does not say
  // it either: only an occurrence still waiting for a review is named as one.
  const subtitle = selected.reviewed
    ? meta
    : t('web:clientDetail.surveys.reportMetaWaiting', { meta });

  return {
    mode: picking ? 'picking' : 'report',
    pair: null,
    title: history.activeForm?.title ?? '',
    subtitle,
    modeNote,
    announcement: modeNote ?? subtitle,
  };
}
